import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useMapBankAccountToEntityOperationMutation } from "#graphql/supergraphHooks";
import { MapBankAccountToEntityOperationMutation } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";

export interface MapBankAccountToEntityParams {
  bankAccountId: string;
  entityId: string;
  arenaId: string;
}

export interface UseMapBankAccountToEntityOptions {
  onSuccess?: (bankAccountId: string, entityId: string) => void;
  onError?: (error: Error) => void;
}

export interface UseMapBankAccountToEntityReturn {
  mapBankAccountToEntity: (params: MapBankAccountToEntityParams) => Promise<boolean>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that handles mapping a bank account to an investment entity.
 * Creates entries in the account_bank_account_map table.
 *
 * @example
 * ```tsx
 * const { mapBankAccountToEntity, loading } = useMapBankAccountToEntity({
 *   onSuccess: (bankAccountId, entityId) => {
 *     console.log('Mapped bank account', bankAccountId, 'to entity', entityId);
 *   },
 * });
 *
 * await mapBankAccountToEntity({
 *   bankAccountId: "QmFua0FjY291bnQ6MTIz",
 *   entityId: "QWNjb3VudDo0NTY="
 * });
 * ```
 */
export const useMapBankAccountToEntity = (
  options: UseMapBankAccountToEntityOptions = {},
): UseMapBankAccountToEntityReturn => {
  const { onSuccess, onError } = options;

  const [mapBankAccountToEntityMutation, { loading, error }] =
    useMapBankAccountToEntityOperationMutation({
      onCompleted: (data: MapBankAccountToEntityOperationMutation) => {
        if (data?.mapBankAccountToEntity?.success && data.mapBankAccountToEntity.mapping) {
          onSuccess?.(
            data.mapBankAccountToEntity.mapping.bankAccountId,
            data.mapBankAccountToEntity.mapping.accountId,
          );
        }
      },
      onError: (apolloError: ApolloError) => {
        onError?.(apolloError);
      },
      context: clientContext(),
    });

  // Report underlying Apollo/GraphQL errors to Datadog. Consumers may also report
  // synthetic "returned false" messages — these provide the underlying details.
  useReportError(error ? formatError(error) : null);

  const mapBankAccountToEntity = useCallback(
    async (params: MapBankAccountToEntityParams): Promise<boolean> => {
      try {
        const result = await mapBankAccountToEntityMutation({
          variables: {
            bankAccountId: params.bankAccountId,
            entityId: params.entityId,
            arenaId: params.arenaId,
          },
        });

        const data = result.data;
        if (data?.mapBankAccountToEntity?.success) {
          if (data.mapBankAccountToEntity.errorMessage) {
            return false;
          }
          return true;
        }

        return false;
      } catch (err) {
        // Apollo errors are forwarded to the consumer's `onError` from the
        // mutation's `onError` callback above. Only forward unexpected
        // non-Apollo errors here so the consumer callback fires exactly once.
        if (!(err instanceof ApolloError)) {
          onError?.(err instanceof Error ? err : new Error("An unexpected error occurred"));
        }
        return false;
      }
    },
    [mapBankAccountToEntityMutation, onError],
  );

  return {
    mapBankAccountToEntity,
    loading,
    error,
  };
};
